import Promise from 'bluebird';
import _ from 'lodash';
import moment from 'moment';

import api from '../api';
import filters from '../core/filters';
import environment from '../environment';
import translate from '../core/tools/translate';
import { notify, NOTIFY_TYPE } from '../shared/notify';

import ShapesControl from './ShapesControl';

const LayerControls = (name) => require('../controls').get(name);
const LayerInfo = (name) => require('../info').get(name);

export class VoronoiControl extends ShapesControl {
    constructor () {
        const options = {
            help: translate(`Help.Voronoi`),
            id: 'voronoi-area',
            name: 'Voronoi',
            type: 'vector-layers-voronoi',
            types: environment.get('CONFIG.FILTERS.VORONOI_TYPES'),
            themes: environment.get('CONFIG.FILTERS.VORONOI_THEMES'),
            filter: false
        };

        super(options);

        this._change = this._change.bind(this);
        this._getLayerOptions = this._getLayerOptions.bind(this);
        this._getTagPropertyValue = this._getTagPropertyValue.bind(this);
        this.add = this.add.bind(this);
        this.colorize = this.colorize.bind(this);
        this.remove = this.remove.bind(this);
        this.select = this.select.bind(this);
    }

    static getInstance () {
        if (!VoronoiControl._instance) {
            VoronoiControl._instance = new VoronoiControl();
        }

        return VoronoiControl._instance;
    }

    get _themes() {
        const value = this.getThemeValue();
        const themes = this.options.themes;

        const group = _.reduce(themes, (acc, group) => {
            if (group && group.id === value && group.id === themes.groups.id) {
                acc[group.id] = 'Grupo';
            }

            if (themes.groupsClients) {
                if (group && group.id === value && group.id === themes.groupsClients.id) {
                    acc[group.id] = 'GrupoCliente';
                }
            }

            return acc;
        }, {});
        return group;
    }

    unhighlight () {
    }

    select() {
        const value = this.getTypeValue(true);

        const { symbology } = this.getLayer().options;

        let options;
        if (value === -1) {
            symbology.scale = 'nomenal';
            options = {
                remote: {},
                voronoi: true,
                showAll: true,
                value: 'Grupo',
                symbology
            };
            this.update();
        } else {
            symbology.scale = 'jenks';
            options = {
                remote: {
                    url: `${environment.get('API_HOST')}/agrupamento/ObterAgrupamentos`,
                    params: {
                        IdTipoAgrupamento: value
                    }
                },
                voronoi: false,
                showAll: false,
                value: 'Total',
                symbology
            };
        }

        return super.select(value, options);
    }

    colorize (featureCollection, layer = this.getLayer()) {
        return new Promise((resolve, reject) => {
            // console.time('NeoGIG.Feature.Shapes -> colorize');

            const id = layer.getMap()._id;
            const themeId = this.getThemeValue(id);

            if (!this.getControls().dropdown.data) {
                return resolve(featureCollection);
            } else if (!_.size(LayerInfo('Voronoi').values().id) && this.getTypeValue(true) !== environment.get('TYPE.DYNAMIC')) {
                notify(NOTIFY_TYPE.LOG, translate('Validation.LocationRequired'));
                return resolve(featureCollection);
            } else if (themeId === 0) {
                return resolve(featureCollection);
            }

            this._cachePages = new Map();

            this.getControls(id).colorpicker.fillColor.input.enable(true);
            this.getControls(id).colorpicker.fillColor.enable = true;
            this.getControls(id).slider.range.input.enable(true);
            this.getControls(id).slider.range.enable = true;
            this.getControls(id).slider.range.input.value(10);
            this.getControls(id).slider.range.value = 10;

            if (this.getTypeValue(true) === environment.get('TYPE.DYNAMIC')) {
                resolve(this._colorizeDynamicShape(id));
            } else {
                if (!this.check() && this.getLayer().visible()) {
                    return;
                }
                this.options.request(this._getLayerOptions(themeId, id)).then(res =>
                    resolve(this._colorizeStaticShape(id, featureCollection, res))
                );
            }
        });
    }

    update (id) {
        if (this.getTypeValue(true) !== environment.get('TYPE.DYNAMIC')) {
            return;
        }

        const l1 = LayerControls('Dealerships').getLayer(id);
        const l2 = LayerControls('Competitors').getLayer(id);
        const l3 = LayerControls('Greenfield').getLayer(id);
        const l4 = LayerControls('PointOfInterest').getLayer(id);
        const l5 = LayerControls('UnapprovedStores').getLayer(id);

        const features = [l1, l2, l3, l4, l5]
            .filter(l => l.visible())
            .reduce((prev, cur) => prev.concat(cur.getCurrentFeatures()), []);

        this.getLayer(id).clearFeatures();

        this.getLayer(id).setFeatures({ features }, this.check());
        this.colorize(features);
    }

    add (features) {
        if (!features || this.getTypeValue(true) !== environment.get('TYPE.AUTO')) return;

        _.forEach(features, f => this.getLayer().addLayer(f, false));

        if (this.check() && this.getLayer().visible()) {
            this.getLayer().reload();
        }
    }

    remove (features) {
        if (!(features && _.isArray(features)) || this.getTypeValue(true) !== -1) {
            return;
        }

        _.forEach(features, f => this.getLayer().removeLayer(f, false));

        if (this.check() && this.getLayer().visible()) {
            this.getLayer().reload();
        }
    }

    _change (e) {
        if (e.checked && this.getTypeValue(true) === environment.get('TYPE.DYNAMIC')) {
            this.update();
        } else {
            super._change(e);
        }
    }

    _changeTheme() {
        super._changeTheme();
        
        if (this.getTypeValue(true) === environment.get('TYPE.DYNAMIC')) {
            this.update();
        } else {
            if (this.check() && this.getLayer().visible()) {
                const features = this.getLayer().toGeoJSON().features;
                this.colorize({ type: 'FeatureCollection', features });
            }
        }
    }

    _getTagPropertyValue (property) {
        switch (property) {
        case 'value':
            const mask = {
                property: this.getTypeValue(true) === environment.get('TYPE.DYNAMIC') ? this._themes[_.toString(this.getThemeValue())] : 'Total',
                mask: (_.find(this.options.themes, t => t.id === this.getThemeValue()) || {}).mask
            };
            return _.assign({}, this._tags.tagValue, mask);
        default:
            return _.assign({}, this._tags.tagName);
        }
    }

    _unhighlightArea () {
        // const { symbology } = this.getLayer().options;
        // symbology.type = 'single';
        this.getLayer().setOptions({
            // symbology,
            legend: false,
            value: null
        });
        if (this.check() && this.getLayer().visible()) {
            return this.getLayer().redraw();
        }
    }

    _colorizeDynamicShape (id) {
        const { symbology } = this.getLayer(id).options;
        // symbology.type = 'choropleth';
        symbology.scale = 'nomenal';
        this.getLayer(id).setOptions({
            value: this._themes[_.toString(this.getThemeValue())],
            legend: {
                title: this.getThemeText(),
                type: 'uniq'
            },
            symbology
        });
        this._setLayerLabelOptions('main', false);
    }

    _getLayerOptions (themeId = this.getThemeValue(), id) {
        const filterData = filters.get(`filter-${_.kebabCase(this.options.name)}-${this.getThemeValue()}`, id);
        let typeId = Number(this.getControls(id).dropdown.list.value);
        if (typeId === environment.get('TYPE.AUTO')) {
            typeId = (this.getZoomLocation('ibge') || {}).id;
        }

        const options = {
            idTema: themeId,
            IdOrigem: LayerInfo('Voronoi').values().id[0],
            idTipo: typeId
        };

        if (filterData) {
            options.anoMesInicio = this.getDate(id).start;
            options.anoMesFim = this.getDate(id).end;
            options.idsBandeiras = filterData.brands;
            options.idsCategorias = filterData.models;
        } else {
            options.anoMesInicio = moment(environment.get('CONFIG.FILTERS.ENDYEAR')).startOf('year').format('YYYYMM');
            options.anoMesFim = moment(environment.get('CONFIG.FILTERS.ENDYEAR')).format('YYYYMM');
        }

        return options;
    }
}

export default VoronoiControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/VoronoiControl.js